import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Building2,
  Plus,
  ShieldQuestion,
  Package,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { tenantsService } from '@/services/platform/tenants';
import { filterNavForUser, navItems } from '@/config/navigation';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { cn } from '@/lib/utils';

interface CommandItem {
  id: string;
  group: 'pages' | 'tenants' | 'actions';
  label: string;
  /** Optional secondary label rendered in a muted monospace badge. */
  hint?: string;
  icon?: LucideIcon;
  /** Effect when the user picks this item. */
  onSelect: () => void;
  /** Searchable haystack — defaults to label + hint. */
  keywords?: string;
}

/**
 * Command palette (⌘K). Linear-style fuzzy launcher: navigate anywhere,
 * jump to any tenant by code, run common actions.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const me = useCurrentUser();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Lazy-fetch tenants only when the palette is open. Cached across opens.
  const tenantsQuery = useQuery({
    queryKey: ['platform', 'tenants', 'palette'],
    queryFn: () => tenantsService.list({ pageSize: 200 }),
    enabled: open,
    staleTime: 60_000,
  });

  // Reset state when opening.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const items = useMemo<CommandItem[]>(() => {
    const visibleNav = filterNavForUser(navItems, me);
    const pages: CommandItem[] = visibleNav.map((n) => ({
      id: `page:${n.to}`,
      group: 'pages',
      label: n.label,
      hint: n.description,
      icon: n.icon,
      onSelect: () => navigate(n.to),
      keywords: `${n.label} ${n.description ?? ''} ${n.to}`,
    }));

    const tenants: CommandItem[] = (tenantsQuery.data?.items ?? []).map((t) => ({
      id: `tenant:${t.id}`,
      group: 'tenants',
      label: t.name,
      hint: t.code,
      icon: Building2,
      onSelect: () => navigate(`/platform/subscriptions/${t.id}`),
      keywords: `${t.name} ${t.code} ${t.contactEmail ?? ''}`,
    }));

    const actions: CommandItem[] = [
      {
        id: 'action:onboard-tenant',
        group: 'actions',
        label: 'Onboard new tenant',
        icon: Plus,
        onSelect: () => navigate('/platform/onboarding'),
        keywords: 'create tenant new onboard',
      },
      {
        id: 'action:new-plan',
        group: 'actions',
        label: 'Create new plan',
        icon: Package,
        onSelect: () => navigate('/platform/plans'),
        keywords: 'create plan new pricing tier package',
      },
      {
        id: 'action:open-session',
        group: 'actions',
        label: 'Open support session',
        icon: ShieldQuestion,
        onSelect: () => navigate('/platform/support-sessions'),
        keywords: 'support session jit access',
      },
    ];

    return [...pages, ...tenants, ...actions];
  }, [me, navigate, tenantsQuery.data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const hay = (it.keywords ?? `${it.label} ${it.hint ?? ''}`).toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  // Group filtered items keeping the relative order, so the visual sections stay sensible.
  const groups = useMemo(() => {
    const g: Record<CommandItem['group'], CommandItem[]> = { pages: [], tenants: [], actions: [] };
    for (const it of filtered) g[it.group].push(it);
    return g;
  }, [filtered]);

  // Keep `active` index inside bounds when the filtered list shrinks.
  useEffect(() => {
    if (active >= filtered.length) setActive(0);
  }, [filtered.length, active]);

  // Keyboard nav inside the palette.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        const it = filtered[active];
        if (it) {
          e.preventDefault();
          it.onSelect();
          onOpenChange(false);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, filtered, active, onOpenChange]);

  // Scroll the active item into view as the user navigates.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(`[data-cmd-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const renderItem = (it: CommandItem, idx: number) => {
    const isActive = idx === active;
    const Icon = it.icon;
    return (
      <li
        key={it.id}
        data-cmd-idx={idx}
        onMouseEnter={() => setActive(idx)}
        onClick={() => {
          it.onSelect();
          onOpenChange(false);
        }}
        className={cn(
          'flex cursor-pointer items-center gap-3 px-3 py-2 text-[13px] transition-colors',
          isActive ? 'bg-muted/80 text-foreground' : 'text-foreground/85',
        )}
      >
        {Icon ? (
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
        ) : (
          <span className="h-4 w-4 shrink-0" />
        )}
        <span className="flex-1 truncate">{it.label}</span>
        {it.hint && (
          <Badge variant="muted" className="font-mono text-[10px] truncate max-w-[160px]">
            {it.hint}
          </Badge>
        )}
        {isActive && <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />}
      </li>
    );
  };

  // Compute global indices so keyboard nav crosses groups in order.
  let globalIdx = -1;
  const renderGroup = (label: string, items: CommandItem[]) => {
    if (items.length === 0) return null;
    return (
      <div className="py-1">
        <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">
          {label}
        </div>
        <ul>
          {items.map((it) => {
            globalIdx += 1;
            return renderItem(it, globalIdx);
          })}
        </ul>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[18%] max-w-xl translate-y-0 gap-0 p-0">
        <div className="flex items-center gap-2 border-b border-border/70 px-3 py-2.5">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            placeholder="Jump to a page, tenant, or action…"
            className="w-full bg-transparent text-[14px] outline-none placeholder:text-muted-foreground/70"
          />
          <Badge variant="muted" className="font-mono text-[10px]">esc</Badge>
        </div>

        <ul ref={listRef} className="max-h-[420px] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <li className="px-4 py-8 text-center text-[12.5px] text-muted-foreground">
              No matches for &quot;{query}&quot;
            </li>
          ) : (
            <>
              {renderGroup('Pages', groups.pages)}
              {renderGroup('Tenants', groups.tenants)}
              {renderGroup('Actions', groups.actions)}
            </>
          )}
        </ul>

        <div className="flex items-center gap-3 border-t border-border/70 bg-muted/30 px-3 py-1.5 text-[10.5px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Badge variant="muted" className="font-mono text-[9px]">↑↓</Badge> navigate
          </span>
          <span className="inline-flex items-center gap-1">
            <Badge variant="muted" className="font-mono text-[9px]">↵</Badge> open
          </span>
          <span className="inline-flex items-center gap-1">
            <Badge variant="muted" className="font-mono text-[9px]">⌘K</Badge> toggle
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Mount once near the app root. Provides a global ⌘K listener and renders
 * the palette. Doesn't expose a context — other components don't need to
 * trigger it, and the `useCommandPalette` hook below covers the topbar's
 * search-button case.
 */
export function CommandPaletteProvider() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Listen for an app-level "open command palette" event so other UI (the
  // topbar's search button) can trigger the same dialog without context.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('zafroi:command-palette-open', onOpen);
    return () => window.removeEventListener('zafroi:command-palette-open', onOpen);
  }, []);

  return <CommandPalette open={open} onOpenChange={setOpen} />;
}

/** Triggers the global command palette open. Any UI can call it. */
export function openCommandPalette() {
  window.dispatchEvent(new CustomEvent('zafroi:command-palette-open'));
}
